import { SCREEN_SIZES } from '@/shared/Constants';

import type { MouseEvent as ReactMouseEvent } from 'react';

export const supportsTouch = (): boolean => {
	if (typeof window === 'undefined' || typeof navigator === 'undefined') return false;
	return (
		'ontouchstart' in window ||
		(typeof navigator.maxTouchPoints === 'number'
			? navigator.maxTouchPoints > 0
			: typeof (navigator as any).msMaxTouchPoints === 'number' &&
			  (navigator as any).msMaxTouchPoints > 0)
	);
};

export const preventMobileHistoryNavigation = (
	event?: TouchEvent | MouseEvent | ReactMouseEvent,
): void => {
	if (!supportsTouch() || !event) return;
	event.preventDefault?.();
	event.stopPropagation?.();
};

// Side element sizes
const SIZE_PHONE = 80;
const SIZE_TABLET = 110;
const SIZE_DESKTOP = 180;

// Inboard offsets (reduce to place side elements closer to the laneside)
const INBOARD_OFFSET_PHONE = 46;
const INBOARD_OFFSET_DESKTOP = 78;

// Initial lift Y values
const INITIAL_LIFT_Y_PHONE = -50;
const INITIAL_LIFT_Y_TABLET = -70;
const INITIAL_LIFT_Y_DESKTOP = -90;

// Side element lane helpers
const LCG_MULTIPLIER = 1664525;
const LCG_INCREMENT = 1013904223;
const DEFAULT_COUNT_PER_SIDE = 2;
const LOOP_MIN_OFFSET_FACTOR = -1 / 6;

// Character jump heights
const JUMP_HEIGHT_PHONE = 260;
const JUMP_HEIGHT_TABLET = 320;
const JUMP_HEIGHT_DESKTOP = 370;

export function computeInitialLiftYFromWidth(): number {
	const w = window.innerWidth;
	if (w < SCREEN_SIZES.DESKTOP) return INITIAL_LIFT_Y_PHONE;
	else if (w < SCREEN_SIZES.LARGE_DESKTOP) return INITIAL_LIFT_Y_TABLET;
	return INITIAL_LIFT_Y_DESKTOP;
}

export function getSideElementSizePx(): number {
	const w = window.innerWidth;
	if (w < SCREEN_SIZES.DESKTOP) return SIZE_PHONE; // phones
	if (w < SCREEN_SIZES.LARGE_DESKTOP) return SIZE_TABLET; // tablets / small laptops
	return SIZE_DESKTOP; // desktops
}

export function getInboardOffsetPx(): number {
	const w = window.innerWidth;
	return w < SCREEN_SIZES.DESKTOP ? INBOARD_OFFSET_PHONE : INBOARD_OFFSET_DESKTOP;
}

export function getCharacterJumpHeightPx(screenWidth?: number): number {
	const fallbackWidth = SCREEN_SIZES.LARGE_DESKTOP;
	const w =
		typeof screenWidth === 'number'
			? screenWidth
			: typeof window !== 'undefined'
			? window.innerWidth
			: fallbackWidth;
	if (w < SCREEN_SIZES.DESKTOP) return JUMP_HEIGHT_PHONE;
	if (w < SCREEN_SIZES.LARGE_DESKTOP) return JUMP_HEIGHT_TABLET;
	return JUMP_HEIGHT_DESKTOP;
}

export type SideElementsRng = {
	nextInt: (max: number) => number;
};

export const createRng = (
	seed: number = Math.floor(Math.random() * 0x7fffffff),
): SideElementsRng => {
	let state = seed >>> 0;
	return {
		nextInt(max: number) {
			state = (state * LCG_MULTIPLIER + LCG_INCREMENT) >>> 0;
			return state % max;
		},
	};
};

export const normalizeCountPerSide = (count?: number): 1 | 2 => {
	const normalized = Math.max(
		1,
		Math.min(2, Number.isFinite(count) ? Number(count) : DEFAULT_COUNT_PER_SIDE),
	);
	return normalized === 1 ? 1 : 2;
};

export type LaneGeometry = {
	bankXAtY: (y: number, side: 'left' | 'right') => number;
	loopMin: number;
	loopMax: number;
	loopSpan: number;
	viewHeight: number;
};

export const getLaneBottomPct = (screenWidth?: number): number => {
	const width =
		screenWidth != null
			? screenWidth
			: typeof window !== 'undefined'
			? window.innerWidth
			: SCREEN_SIZES.DESKTOP;
	if (width < SCREEN_SIZES.MOBILE) return 130;
	return width < SCREEN_SIZES.TABLET ? 110 : 95;
};

export const getLaneTopPct = (screenWidth?: number): number => {
	const width =
		screenWidth != null
			? screenWidth
			: typeof window !== 'undefined'
			? window.innerWidth
			: SCREEN_SIZES.DESKTOP;
	if (width < SCREEN_SIZES.MOBILE) return 60;
	return width < SCREEN_SIZES.TABLET ? 50 : 40;
};

export const getSideElementsTopPct = (screenWidth?: number): number => {
	const width =
		screenWidth != null
			? screenWidth
			: typeof window !== 'undefined'
			? window.innerWidth
			: SCREEN_SIZES.DESKTOP;
	if (width < SCREEN_SIZES.MOBILE) return 55;
	return width < SCREEN_SIZES.TABLET ? 45 : 35;
};

export const createLaneGeometry = ({
	width,
	height,
	topWidthPct,
	bottomWidthPct,
	elementSize,
}: {
	width: number;
	height: number;
	topWidthPct: number;
	bottomWidthPct: number;
	elementSize: number;
}): LaneGeometry => {
	const centerX = width / 2;
	const bottomWidthPx = (width * bottomWidthPct) / 100;
	const topWidthPx = (width * topWidthPct) / 100;
	const halfBottom = bottomWidthPx / 2;
	const halfTop = topWidthPx / 2;
	const bottomY = height;
	const curveY = height * 0.2;
	const ctrlOffset = Math.max(2, width * 0.02);
	const rightCtrlX = centerX + (halfTop + halfBottom) / 2 + ctrlOffset;
	const leftCtrlX = centerX - (halfTop + halfBottom) / 2 - ctrlOffset;

	const solveTForY = (y: number): number => {
		const a = bottomY - 2 * curveY;
		const b = 2 * curveY;
		const c = -y;
		if (Math.abs(a) < 1e-6) {
			return b !== 0 ? y / b : 0;
		}
		const D = b * b - 4 * a * c;
		const sqrtD = Math.sqrt(Math.max(0, D));
		const t1 = (-b + sqrtD) / (2 * a);
		const t2 = (-b - sqrtD) / (2 * a);
		if (t1 >= 0 && t1 <= 1) return t1;
		if (t2 >= 0 && t2 <= 1) return t2;
		return Math.abs(t1 - 0.5) < Math.abs(t2 - 0.5) ? t1 : t2;
	};

	const bezierX = (t: number, p0x: number, cx: number, p1x: number) => {
		const mt = 1 - t;
		return mt * mt * p0x + 2 * mt * t * cx + t * t * p1x;
	};

	const bankXAtY = (y: number, side: 'left' | 'right'): number => {
		const t = solveTForY(y + elementSize / 2);
		if (side === 'right') {
			return bezierX(t, centerX + halfTop, rightCtrlX, centerX + halfBottom);
		}
		return bezierX(t, centerX - halfTop, leftCtrlX, centerX - halfBottom);
	};

	const loopMin = elementSize * LOOP_MIN_OFFSET_FACTOR;
	const loopMax = height + elementSize / 2;
	const loopSpan = loopMax - loopMin;

	return {
		bankXAtY,
		loopMin,
		loopMax,
		loopSpan,
		viewHeight: height,
	};
};

export type SideElementItem = {
	id: string;
	y: number;
	xSide: 'left' | 'right';
	jitter: number;
	image: string;
};

export function computeBottomLaneCenters({
	width,
	lanes,
	laneBottomPct,
}: {
	width: number;
	lanes: number;
	laneBottomPct: number;
}): number[] {
	const safeWidth = Math.max(1, width);
	const lanesCount = Math.max(1, Math.min(4, Math.round(lanes || 4)));
	const bottomW = Math.max(18, laneBottomPct);
	const bottomWidthPx = (safeWidth * bottomW) / 100;
	const halfBottom = bottomWidthPx / 2;
	const centerX = safeWidth / 2;
	const leftX = centerX - halfBottom;
	const widthPx = bottomWidthPx;

	const shiftTowardLeftLine = lanesCount === 2 ? 0.2 : 0.25;
	const shiftTowardRightLine = lanesCount === 2 ? -0.2 : -0.25;
	const secondBoxLeftNudge = 0.02;
	const thirdBoxRightNudge = -0.02;

	const centers: number[] = [];
	for (let i = 0; i < lanesCount; i++) {
		const baseFrac = (i + 0.5) / lanesCount;
		let withinLaneShift = 0;
		if (i === 0 && lanesCount > 1) withinLaneShift = shiftTowardLeftLine / lanesCount;
		else if (i === lanesCount - 1 && lanesCount > 1)
			withinLaneShift = shiftTowardRightLine / lanesCount;
		else if (i === 1) withinLaneShift = secondBoxLeftNudge;
		else if (i === 2) withinLaneShift = thirdBoxRightNudge;
		const frac = Math.min(0.98, Math.max(0.01, baseFrac + withinLaneShift));
		const x = leftX + frac * widthPx;
		centers.push(x);
	}
	return centers;
}

export const createInitialItems = ({
	sides,
	geometry,
	itemsPerSide,
	elementSize,
	rng,
	sprites,
}: {
	sides: 'left' | 'right' | 'both';
	geometry: LaneGeometry;
	itemsPerSide: 1 | 2;
	elementSize: number;
	rng: SideElementsRng;
	sprites?: string[];
}): SideElementItem[] => {
	const items: SideElementItem[] = [];
	const gap = (geometry.viewHeight + elementSize) / itemsPerSide;
	const sideAnchors: Record<'left' | 'right', number> = {
		left: -elementSize / 2, // left bank should emerge from the top
		right: geometry.viewHeight / 2, // right bank starts midway down the lane
	};
	const spritePool = sprites?.length ? sprites : [];
	const defaultSprite = spritePool[0] ?? '';
	const buildSpriteQueue = (): string[] => {
		if (!spritePool.length) return [defaultSprite];
		const pool = [...spritePool];
		for (let i = pool.length - 1; i > 0; i -= 1) {
			const j = rng.nextInt(i + 1);
			const tmp = pool[i];
			pool[i] = pool[j];
			pool[j] = tmp;
		}
		return pool;
	};

	let spriteQueue = buildSpriteQueue();
	const drawSprite = (exclude?: string): string => {
		if (!spritePool.length) return defaultSprite;
		let attempts = 0;
		while (attempts < Math.max(1, spritePool.length)) {
			if (!spriteQueue.length) {
				spriteQueue = buildSpriteQueue();
			}
			const sprite = spriteQueue.shift() ?? defaultSprite;
			if (sprite !== exclude || attempts === spritePool.length - 1) {
				return sprite;
			}
			spriteQueue.push(sprite);
			attempts++;
		}
		return defaultSprite;
	};

	const addSide = (sideKey: 'left' | 'right') => {
		const anchorY = sideAnchors[sideKey];
		const phaseOffset = sideKey === 'right' ? gap / 2 : 0; // stagger right bank so sides alternate in view
		let lastSprite: string | undefined;
		for (let i = 0; i < itemsPerSide; i++) {
			const image = drawSprite(lastSprite);
			lastSprite = image;
			items.push({
				id: `${sideKey}-${i}`,
				y: anchorY + phaseOffset + i * gap,
				xSide: sideKey,
				jitter: (Math.random() - 0.5) * 6,
				image,
			});
		}
	};
	if (sides === 'left' || sides === 'both') addSide('left');
	if (sides === 'right' || sides === 'both') addSide('right');
	return items;
};

const pickSpriteForWrap = (
	current: string,
	used: Set<string>,
	rng: SideElementsRng,
	sprites: string[],
): string => {
	if (!sprites.length) return current;
	const allowReuse = used.size >= sprites.length;
	let candidateIndex = rng.nextInt(sprites.length);
	let attempts = 0;
	while (attempts < sprites.length) {
		const candidate = sprites[candidateIndex];
		const available = allowReuse || !used.has(candidate);
		if (candidate !== current && available) {
			return candidate;
		}
		candidateIndex = (candidateIndex + 1) % sprites.length;
		attempts++;
	}
	return sprites[candidateIndex];
};

export const wrapAndAdvanceItems = ({
	items,
	motionScale,
	motionDirection,
	baseSpeedPxPerSec,
	deltaTime,
	rng,
	loopMin,
	loopMax,
	loopSpan,
	sprites,
}: {
	items: SideElementItem[];
	motionScale: number;
	motionDirection: 1 | -1;
	baseSpeedPxPerSec: number;
	deltaTime: number;
	rng: SideElementsRng;
	loopMin: number;
	loopMax: number;
	loopSpan: number;
	sprites?: string[];
}): SideElementItem[] => {
	const spritePool = sprites?.length ? sprites : [];
	const speed = baseSpeedPxPerSec * Math.max(0, motionScale);
	const dir = motionDirection === -1 ? -1 : 1;
	const dy = speed * deltaTime * dir;
	const next: SideElementItem[] = [];
	items.forEach((item, idx) => {
		let y = item.y + dy;
		let wrapped = false;
		if (y < loopMin) {
			y += loopSpan;
			wrapped = true;
		} else if (y > loopMax) {
			y -= loopSpan;
			wrapped = true;
		}
		let image = item.image;
		if (wrapped) {
			// Only track sprites used on the SAME side to avoid repetition
			const used = new Set<string>();
			next.forEach((assigned) => {
				if (assigned.xSide === item.xSide) {
					used.add(assigned.image);
				}
			});
			for (let i = idx + 1; i < items.length; i += 1) {
				if (items[i].xSide === item.xSide) {
					used.add(items[i].image);
				}
			}
			image = pickSpriteForWrap(image, used, rng, spritePool);
		}
		next.push({ ...item, y, image });
	});
	return next;
};
